import type { RuleArea } from "@unbox/api";
import { useDeleteBlocklistRule } from "@/api/blocklist-rules";
import { useDeleteCohortRule } from "@/api/cohort-rules";
import { useDeleteSampleRule } from "@/api/sample-rules";
import { Button } from "@/components/base/buttons/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { pluralize } from "@/utils/text";

export type DeleteRuleTarget = {
  ruleId: string;
  area: RuleArea;
  name: string;
  impactedCount: number | undefined;
};

const CONSEQUENCES: Record<RuleArea, { counted: string; uncounted: string }> = {
  cohort: {
    counted: "are tagged by this rule. They lose the tag unless another enabled rule also tags them.",
    uncounted: "Creators tagged by this rule lose the tag, unless another enabled rule also tags them.",
  },
  sample: {
    counted:
      "are auto-approved by this rule. Their requests go back to manual review unless another enabled rule also approves them.",
    uncounted:
      "Requests auto-approved by this rule go back to manual review, unless another enabled rule also approves them.",
  },
  blocklist: {
    counted: "are blocked by this rule. They're unblocked unless another enabled rule also blocks them.",
    uncounted: "Creators blocked by this rule are unblocked, unless another enabled rule also blocks them.",
  },
};

export const DeleteRuleDialog = ({
  target,
  onClose,
}: {
  target: DeleteRuleTarget | undefined;
  onClose: () => void;
}) => {
  const deleteCohortRule = useDeleteCohortRule();
  const deleteSampleRule = useDeleteSampleRule();
  const deleteBlocklistRule = useDeleteBlocklistRule();

  const deletions: Record<RuleArea, typeof deleteCohortRule> = {
    cohort: deleteCohortRule,
    sample: deleteSampleRule,
    blocklist: deleteBlocklistRule,
  };
  const deletion = target ? deletions[target.area] : undefined;

  const consequence = target ? CONSEQUENCES[target.area] : undefined;
  const counted = target?.impactedCount ? pluralize(target.impactedCount, "creator", "creators") : null;

  const confirm = async () => {
    if (!target) return;
    await deletions[target.area].mutateAsync(target.ruleId);
    onClose();
  };

  return (
    <Dialog open={!!target} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete “{target?.name}”?</DialogTitle>
          <DialogDescription>
            {counted && <strong className="font-semibold text-primary">{counted} </strong>}
            {counted ? consequence?.counted : consequence?.uncounted}
            <span className="mt-2 block font-semibold text-secondary">This can't be undone.</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button color="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button color="primary-destructive" size="sm" isLoading={deletion?.isPending} onClick={confirm}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
